import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "@tanstack/react-router";
// Import Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// State Import
import { useMain } from "../../state/userMainState";
// import { useBranchList } from "../../state/branchListState";
import { useUserBranches } from "../../state/userBranchListState";
// import { useContractList } from "../../state/contractListState";
import { useStaffCardContractList } from "../../state/staffCardContractListState";
// import { useWardrobeList } from "../../state/wardrobeListState";
import { useStaffCardWardrobeList } from "../../state/staffCardWardrobeListState";
// Hooks
import { useStaffAdd } from "../../hooks/useStaffAdd";
import { useStaffUpdate } from "../../hooks/useStaffUpdate";
import { useCheckAPI } from "../../hooks/useCheckAPI";

// MUI
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";

// Components
// import { Loading } from "../../components/Loading";
import { RHFAutocompleteField } from "./RHFAutocompleteField";
import { RHFInputField } from "./RHFInputField";
import { RHFSelectField } from "../../components/RHFSelectField";
import { ComponentAuthorisation } from "../../components/ComponentAuthorisation";

export const StaffCardForm = ({
  staffData,
  newStaff,
  loading,
  isModal,
  handleModalClose,
}) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const [viewMode, setViewMode] = useState(!newStaff);
  const router = useRouter();
  const [wardrobes, setWardobes] = useState([]);

  // Fetch Main data
  const { isFetching, isError, isSuccess, error, data } = useMain();

  // Main Data
  const [mainData, setMainData] = useState({
    user: {},
    branches: [],
    bodyTypes: [],
    countries: [],
  });

  // API call success
  const successFunc = () => setMainData(data.data.data.data.data);

  // API call error
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  let defaultValues = {};

  if (!newStaff) {
    const currentStaff = staffData.staff;
    // Business Central sends null for anything unset, which would put the
    // inputs into uncontrolled mode.
    defaultValues = {
      staffid: currentStaff.staffid ?? "",
      name: currentStaff.name ?? "",
      branch: currentStaff.branch ?? "",
      gender: currentStaff.gender ?? "",
      uniform: currentStaff.uniform ?? "",
      yourid: currentStaff.yourid ?? "",
      contract: currentStaff.contract ?? "",
      email: currentStaff.email ?? "",
      blocked: currentStaff.blocked ?? "",
    };
  }

  if (newStaff) {
    defaultValues = {
      name: "",
      branch: "",
      gender: "",
      uniform: "",
      yourid: "",
      contract: "",
      email: "",
      blocked: "No",
    };
  }

  const resetValues = {
    name: "",
    branch: "",
    gender: "",
    uniform: "",
    yourid: "",
    contract: "",
    email: "",
    blocked: "",
  };

  const { handleSubmit, reset, control } = useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [staffData, reset]);

  // Form Validation text
  const validationHelper = {
    name: { required: "Name is required" },
    branch: { required: "Branch is required" },
    gender: { required: "Item Type is required" },
    uniform: { required: "Wardrobe is required" },
    yourid: { required: "Wearer ID is required" },
    contract: { required: "Contract is required" },
    email: {
      required: "Email is Required",
      pattern: "Invalid Email Address",
    },
    blocked: { required: "Blocked is required" },
  };

  // Update Staff Mutation
  const {
    mutate: updateStaffSave,
    isSuccess: updateStaffSuccess,
    data: updateStaffData,
  } = useStaffUpdate();

  // Add Staff Mutation
  const {
    mutate: addStaffSave,
    isSuccess: addStaffSuccess,
    data: addStaffData,
  } = useStaffAdd();

  const addStaffSubmit = (data) => {
    addStaffSave({
      data: {
        // filter: { f_staffId: null },
        staff: data,
      },
    });
  };

  const updateStaffSubmit = (data) => {
    updateStaffSave({
      data: {
        filter: { f_staffId: data.staffid },
        staff: data,
      },
    });
  };

  const handleOnSubmit = (data) => {
    if (newStaff) {
      addStaffSubmit(data);
    } else {
      updateStaffSubmit(data);
    }
  };

  const addStaffSuccessFunc = () => {
    setViewMode(true);
    setAlertNotification({
      message: `New staff member created`,
      severity: "success",
    });
    if (isModal) {
      handleModalClose();
    } else {
      router.history.back();
    }
  };

  const addStaffErrorFunc = () => {
    setAlertNotification({
      message: `${addStaffData.data.statusText}`,
      severity: "warning",
    });
  };

  // Hook to check the BC API for errors
  useCheckAPI(
    addStaffSuccess,
    addStaffData,
    addStaffData?.data?.status,
    addStaffSuccessFunc,
    addStaffErrorFunc,
  );

  const updateStaffSuccessFunc = () => {
    setViewMode(true);
    router.history.back();
    setAlertNotification({
      message: `Staff member updated`,
      severity: "success",
    });
  };

  const updateStaffErrorFunc = () => {
    setAlertNotification({
      message: `${updateStaffData.data.statusText}`,
      severity: "warning",
    });
  };

  // Hook to check the BC API for errors
  useCheckAPI(
    updateStaffSuccess,
    updateStaffData,
    updateStaffData?.data?.status,
    updateStaffSuccessFunc,
    updateStaffErrorFunc,
  );

  // Reset the form
  const handleReset = () => {
    reset(resetValues);
  };

  const handleEditMode = () => {
    setViewMode(false);
  };

  const NameInput = () => {
    return (
      <RHFInputField
        name="name"
        label="Name"
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={viewMode}
      />
    );
  };

  const IdInput = () => {
    return (
      <RHFInputField
        name="yourid"
        label="Wearer ID"
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={viewMode}
      />
    );
  };

  const { data: dataBranches } = useUserBranches();
  const branchArray = dataBranches?.data.data.data.list.branch;

  const BranchSelect = () => {
    return (
      <>
        {dataBranches ? (
          <RHFAutocompleteField
            name="branch"
            label="Branch"
            options={branchArray}
            optionIdentifiers={{ id: "no", label: "name" }}
            control={control}
            validationHelper={validationHelper}
            rules={{ required: true }}
            viewMode={viewMode}
            // groupBy={(option) => option.firstLetter}
          />
        ) : (
          <Skeleton variant="rounded" width="100%" height={50} />
        )}
      </>
    );
  };

  const { data: dataWardrobes, isSuccess: isSuccessWardrobes } =
    useStaffCardWardrobeList();
  // const wardrobeArray = dataWardrobes?.data.data.data.list.wardrobe;

  // API call success
  const successFuncWardrobes = () => {
    const sortedWardrobeArray =
      dataWardrobes?.data.data.data.list.wardrobe.sort((a, b) =>
        a.description > b.description ? 1 : -1,
      );
    setWardobes(sortedWardrobeArray);
  };

  // API call error
  const errorFuncWardrobes = () =>
    console.warn(`Error: ${data.data.status}: ${data.data.statusText}`);

  // Hook to check the BC API for errors
  useCheckAPI(
    isSuccessWardrobes,
    dataWardrobes,
    dataWardrobes?.data?.status,
    successFuncWardrobes,
    errorFuncWardrobes,
  );

  const WardrobeSelect = () => {
    return (
      <>
        {dataWardrobes ? (
          <RHFAutocompleteField
            name="uniform"
            label="Wardrobe"
            // options={wardrobeArray}
            options={wardrobes}
            optionIdentifiers={{ id: "id", label: "description" }}
            control={control}
            validationHelper={validationHelper}
            rules={{ required: true }}
            viewMode={viewMode}
          />
        ) : (
          <Skeleton variant="rounded" width="100%" height={50} />
        )}
      </>
    );
  };

  const { data: dataContracts } = useStaffCardContractList();
  const contractArray = dataContracts?.data.data.data.list.contract;

  const ContractSelect = () => {
    return (
      <>
        {dataContracts ? (
          <RHFAutocompleteField
            name="contract"
            label="Contract"
            options={contractArray}
            optionIdentifiers={{ id: "no", label: "description" }}
            control={control}
            validationHelper={validationHelper}
            rules={{ required: true }}
            viewMode={viewMode}
          />
        ) : (
          <Skeleton variant="rounded" width="100%" height={50} />
        )}
      </>
    );
  };

  const ItemTypeSelect = () => {
    return (
      <>
        {mainData.bodyTypes && mainData.bodyTypes.length > 0 ? (
          <RHFSelectField
            name="gender"
            label="Item Types"
            options={mainData.bodyTypes}
            optionIdentifiers={{ id: "code", label: "description" }}
            control={control}
            validationHelper={validationHelper}
            rules={{ required: true }}
            viewMode={viewMode}
          />
        ) : (
          <Skeleton variant="rounded" width="100%" height={50} />
        )}
      </>
    );
  };

  const EmailInput = () => {
    return (
      <RHFInputField
        name="email"
        label="Email"
        control={control}
        validationHelper={validationHelper}
        rules={{
          required: false,
          pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        }}
        viewMode={viewMode}
      />
    );
  };

  const BlockedSelect = () => {
    return newStaff ? null : (
      <RHFSelectField
        name="blocked"
        label="Blocked"
        options={[
          { id: "Yes", description: "Yes" },
          { id: "No", description: "No" },
        ]}
        optionIdentifiers={{ id: "id", label: "description" }}
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={viewMode}
      />
    );
  };

  const formFields = (
    <>
      <NameInput />
      <IdInput />
      <EmailInput />
      <BranchSelect />
      <ContractSelect />
      <WardrobeSelect />
      <ItemTypeSelect />
      <BlockedSelect />
      {viewMode ? (
        <ComponentAuthorisation allowedRoles={["staffEdit"]}>
          <Button type="button" variant="outlined" onClick={handleEditMode}>
            Edit
          </Button>
        </ComponentAuthorisation>
      ) : (
        <>
          <Button type="button" variant="outlined" onClick={handleReset}>
            Clear Form
          </Button>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </>
      )}
    </>
  );

  // Inside a Dialog the surrounding paper already frames the form, so skip the extra Card there.
  if (isModal) {
    return (
      <Stack
        component="form"
        spacing={2}
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        {formFields}
      </Stack>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
      <Card>
        <Stack
          component="form"
          spacing={2}
          autoComplete="off"
          onSubmit={handleSubmit(handleOnSubmit)}
          sx={{ p: 3 }}
        >
          {formFields}
        </Stack>
      </Card>
    </Box>
  );
};
